import React, { useEffect } from 'react';
import {
  BillingOption,
  Box,
  Typography,
  useRightSideBar
} from '@p81-common/ui';
import { WarningAttentionBox } from '../ui';
import { createClasses } from './styles';

export interface ModeOption<V extends string> {
  value: V;
  label: string;
  description: string;
  disabled?: boolean;
}

interface ModeSelectScreenProps<V extends string> {
  warningTitle?: string;
  warningBody?: string;
  sectionTitle?: string;
  options: ModeOption<V>[];
  value: V | null;
  onChange: (next: V) => void;
  radioName: string;
  nextLabel: string;
  cancelLabel: string;
  onNext: () => void;
  onCancel: () => void;
  subtitle?: string;
}

const ModeSelectScreen = <V extends string>({
  warningTitle,
  warningBody,
  sectionTitle,
  options,
  value,
  onChange,
  radioName,
  nextLabel,
  cancelLabel,
  onNext,
  onCancel,
  subtitle
}: ModeSelectScreenProps<V>) => {
  const { setShowBottomActionBar } = useRightSideBar();
  const classes = createClasses();

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: nextLabel,
        disabled: value === null,
        onClick: onNext
      },
      cancelButtonProps: {
        text: cancelLabel,
        onClick: onCancel
      },
      isSideBar: true
    });

    return () => {
      setShowBottomActionBar(null);
    };
  }, [nextLabel, cancelLabel, value, onNext, onCancel, setShowBottomActionBar]);

  return (
    <Box className={classes.container}>
      {subtitle && (
        <Typography variant="subtitle1" className={classes.subtitle}>
          {subtitle}
        </Typography>
      )}
      {warningTitle && (
        <WarningAttentionBox title={warningTitle} subtitle={warningBody} />
      )}
      {sectionTitle && (
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {sectionTitle}
        </Typography>
      )}
      <div className={classes.optionsContainer}>
        {options.map(opt => (
          <div key={opt.value} className={classes.optionRow}>
            <BillingOption
              label={opt.label}
              checked={value === opt.value}
              onClick={() => onChange(opt.value)}
              radioProps={{
                name: radioName,
                value: opt.value,
                disabled: opt.disabled,
                formControlLabelProps: {
                  label: (
                    <>
                      <Typography
                        component="span"
                        display="block"
                        sx={{
                          fontSize: '15px',
                          lineHeight: '19px',
                          fontWeight: 500,
                          mb: '4px'
                        }}
                      >
                        {opt.label}
                      </Typography>
                      <Typography
                        component="span"
                        display="block"
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: '19px' }}
                      >
                        {opt.description}
                      </Typography>
                    </>
                  )
                }
              }}
            />
          </div>
        ))}
      </div>
    </Box>
  );
};

export default ModeSelectScreen;
